import { formatVolumeCap, volumeCapExportValue } from "utilities/volumeCap";
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useMids } from "hooks/crm/useMids";
import { SearchProvider, useSearch } from "context/SearchContext";
import { countActiveFilters, filterFieldsFor } from "utilities/listFilters";
import { MID_STATUS, statusToneFrom } from "utilities/statusTones";
import RecordTable from "components/Records/RecordTable";
import RecordStatusPill from "components/Records/RecordStatusPill";
import ListSearchBar from "components/Records/ListSearchBar";
import ListPagination from "components/Records/ListPagination";
import { toCsv, csvFilename, downloadCsv } from "utilities/csv";
import RelativeTime from "components/RelativeTime";
import ContainerScopeNotice from "components/ContainerScopeNotice";
import SourceBadge from "components/SourceBadge";
import MidImportModal from "components/Mids/MidImportModal";
import { CreditCard, Download, Upload } from "lucide-react";

const midTone = statusToneFrom(MID_STATUS);
const FIELDS = filterFieldsFor("companyMids");

function MidsTabInner({ companyId, isPartnerView }) {
  const navigate = useNavigate();
  const { filters, set } = useSearch();
  const { user } = useAuth();
  const internal = isInternal(user);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [sort, setSort] = useState("-createdAt");

  // companyId is FORCED, not filtered: it comes from the record you are on, and
  // the scoped namespace has no companyId field for a user filter to collide
  // with. The API expands containers server-side — a partner resolves to its
  // attributed clients, a parent to itself plus children.
  const { mids, pagination, loading, error, refetch, fetchAll } = useMids({
    filters: { ...filters, companyId },
    page,
    limit,
    sort,
  });

  const [exporting, setExporting] = useState(false);
  // SOUND-652 Phase 1: internal only. A partner view is a container of OTHER
  // companies' MIDs, so there is no single company to import into.
  const canImport = internal && !isPartnerView;
  const [importOpen, setImportOpen] = useState(false);

  const prevFiltersRef = React.useRef(filters);
  React.useEffect(() => {
    if (prevFiltersRef.current !== filters) {
      setPage(1);
      prevFiltersRef.current = filters;
    }
  }, [filters]);

  const activeFilterCount = countActiveFilters(filters, FIELDS);

  // A container company (partner or parent) owns no MIDs itself — the rows
  // belong to related companies. Derived from the DATA rather than the company
  // type: any row whose companyId differs means this is an aggregated view. That
  // covers partner (SOUND-395) and parent (SOUND-399) without the tab needing to
  // know which it is, and it is what decides whether the Client column earns its
  // place. On a leaf client every row would say the same name.
  const aggregated = mids.some((m) => m.companyId && m.companyId !== companyId);

  const columns = useMemo(
    () => [
      aggregated && {
        key: "client",
        header: "Client",
        width: "17%",
        exportValue: (m) => m.clientName,
        cell: (m) => <div className="truncate font-medium">{m.clientName || "—"}</div>,
      },
      {
        key: "name",
        header: "Name",
        width: aggregated ? "15%" : "20%",
        sortKey: "name",
        cell: (m) => <div className="truncate font-medium">{m.name || "—"}</div>,
      },
      {
        key: "mid",
        header: "MID",
        width: aggregated ? "14%" : "18%",
        sortKey: "mid",
        className: "font-mono text-[11px] tabular-nums text-muted-foreground",
        exportAs: "text",
        cell: (m) => m.mid || "—",
      },
      {
        key: "monthlyVolumeCap",
        header: "Volume cap",
        width: "10%",
        sortKey: "monthlyVolumeCap",
        align: "right",
        exportValue: volumeCapExportValue,
        cell: formatVolumeCap,
      },
      {
        key: "processorName",
        header: "Processor",
        width: aggregated ? "12%" : "16%",
        sortKey: "processorName",
        cell: (m) => m.processorName || "—",
      },
      // Source is an internal concern — it names which partner sourced the
      // merchant, which is not a client's or a partner's business to read here.
      internal && {
        key: "source",
        header: "Source",
        width: "12%",
        headerClassName: "hidden md:table-cell",
        className: "hidden md:table-cell",
        exportValue: (m) => m.source?.type || "",
        cell: (m) => <SourceBadge source={m.source} />,
      },
      {
        key: "status",
        header: "Status",
        width: "10%",
        sortKey: "status",
        exportValue: (m) => MID_STATUS[m.status]?.label || m.status || "",
        cell: (m) => <RecordStatusPill status={m.status} toneFor={midTone} />,
      },
      {
        key: "effectiveDate",
        header: "Effective",
        width: "10%",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell text-muted-foreground",
        // Blank when nobody entered one. The cell falls back to "Added <createdAt>"
        // for display, but a FILE claiming that as the effective date would be
        // re-imported as a real one.
        exportValue: (m) => m.effectiveDate || "",
        cell: (m) =>
          m.effectiveDate ? (
            <RelativeTime value={m.effectiveDate} />
          ) : (
            <span
              className="opacity-60"
              title="No effective date recorded — showing when the MID was added to the CRM"
            >
              Added <RelativeTime value={m.createdAt} />
            </span>
          ),
      },
    ],
    [aggregated, internal]
  );

  // SOUND-652: Export must round-trip into Import, which requires descriptor,
  // MCC and CAID. These are EXPORT-ONLY — spliced after MID for the file, never
  // rendered in the table. Identifiers go out as ="..." text so Excel keeps
  // their leading zeros (MCC 0742 is real).
  const exportColumns = useMemo(() => {
    const extra = [
      { key: "descriptor", header: "Descriptor" },
      { key: "mcc", header: "MCC", exportAs: "text" },
      { key: "caid", header: "CAID", exportAs: "text" },
      { key: "bin", header: "BIN", exportAs: "text" },
    ];
    const at = columns.findIndex((c) => c && c.key === "mid");
    return [...columns.slice(0, at + 1), ...extra, ...columns.slice(at + 1)];
  }, [columns]);

  // Exports the FILTERED, COMPANY-SCOPED set — fetchAll shares buildParams with
  // the paged fetch, so the forced companyId is already in it and a tab export
  // can never reach beyond the record you are on.
  const handleExport = async () => {
    setExporting(true);
    try {
      const { rows, truncated } = await fetchAll();
      downloadCsv(csvFilename("mids"), toCsv(rows, exportColumns));
      if (truncated) {
        window.alert(
          `Export capped at ${rows.length.toLocaleString()} rows of ${pagination.total.toLocaleString()}. Narrow the filters and export again.`
        );
      }
    } catch {
      window.alert("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {(isPartnerView || aggregated) && (
        <ContainerScopeNotice variant={isPartnerView ? "partner" : "parent"} noun="MIDs" />
      )}

      <ListSearchBar
        value={filters.search || ""}
        onChange={(v) => set("search", v)}
        placeholder="Search MID, name, or DBA"
      >
        <select
          className="select w-auto"
          value={filters.status || ""}
          onChange={(e) => set("status", e.target.value)}
          aria-label="Status"
        >
          <option value="">Status: Any</option>
          {Object.entries(MID_STATUS).map(([value, { label }]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-outline"
          onClick={handleExport}
          disabled={exporting || loading || !pagination.total}
          title="Download this company's filtered MIDs as CSV"
        >
          <Download className="h-4 w-4" />
          {exporting ? "Exporting…" : "Export"}
        </button>
        {canImport && (
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setImportOpen(true)}
            title="Import MIDs into this company from a CSV"
          >
            <Upload className="h-4 w-4" />
            Import
          </button>
        )}
      </ListSearchBar>

      <RecordTable
        columns={columns}
        rows={mids}
        rowKey={(m) => m.midId}
        onRowClick={(m) => navigate(`/mids/${m.midId}`)}
        loading={loading}
        error={error}
        onRetry={refetch}
        filtered={activeFilterCount > 0}
        sort={sort}
        onSortChange={(next) => {
          setSort(next);
          setPage(1);
        }}
        noMatch={{ icon: CreditCard, heading: "No matching MIDs" }}
        empty={{
          icon: CreditCard,
          heading: isPartnerView
            ? "No MIDs across this partner's attributed clients yet"
            : "No MIDs found for this company",
          errorText: "Failed to load MIDs.",
        }}
      />

      <ListPagination
        page={pagination.page || page}
        totalPages={pagination.totalPages || 0}
        total={pagination.total || 0}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={setLimit}
      />

      {canImport && (
        <MidImportModal
          open={importOpen}
          companyId={companyId}
          onClose={(imported) => {
            setImportOpen(false);
            if (imported) refetch();
          }}
        />
      )}
    </div>
  );
}

export default function MidsTab({ companyId, isPartnerView = false }) {
  return (
    <SearchProvider namespace="companyMids">
      <MidsTabInner companyId={companyId} isPartnerView={isPartnerView} />
    </SearchProvider>
  );
}
